import { Component, inject, input, output, ChangeDetectionStrategy } from "@angular/core";
import { CommonModule } from "@angular/common";
import { MatIconModule } from "@angular/material/icon";
import { MatTooltipModule } from "@angular/material/tooltip";
import { toObservable } from "@angular/core/rxjs-interop";
import { DeviceService } from "../../../../../features/device/device.service";
import { of, switchMap, withLatestFrom } from "rxjs";
import { distinctUntilChanged, map, startWith } from "rxjs/operators";
import { TranslocoService } from "@jsverse/transloco";
import { LorenDevice } from "../../../../../features/loren-devices/interfaces/loren-device.interface";
import { LorenDateUtilsService } from "../../../../services/loren-date-utils-service/loren-date-utils.service";

type BatteryLevel = "LOW" | "MEDIUM" | "NORMAL" | "CHARGING" | "UNKNOWN";


@Component({
  selector: "battery-level",
  templateUrl: "./battery-level.html",
  styleUrls: ["./battery-level.css"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [CommonModule, MatIconModule, MatTooltipModule],
})
export class BatteryLevelComponent {
  private readonly deviceService = inject(DeviceService);
  private readonly transloco = inject(TranslocoService);
  protected readonly icons: Record<BatteryLevel, string> = {
    'LOW': 'battery_1_bar',
    'MEDIUM': "battery_3_bar",
    'NORMAL': "battery_full",
    'CHARGING': "battery_charging_full",
    'UNKNOWN': "battery_0_bar",
  }


  clicked = output();
  deviceId = input<number | undefined>(undefined);


  deviceId$ = toObservable(this.deviceId).pipe(
    distinctUntilChanged()
  );

  device$ = this.deviceId$.pipe(
    switchMap(id => {
      if (id === undefined) {
        return of(undefined);
      }

      return this.deviceService.get(+id)
    }),
    map(device => {
      // TODO: This belongs to the back-end logic
      if (device === undefined) {
        return undefined;
      }

      if (device.batteryLevelTime === undefined || device.batteryLevelMaxUpdateInterval === undefined) {
        // Fallback to showing (if we don't know the time ot last measurement)
        // or if we don't have a measurement interval
        return device;
      }

      // Ignore devices with old battery data
      const now = new Date();
      const lastUpdate = LorenDateUtilsService.getMomentUtc(device.batteryLevelTime).toDate();
      const diff = (now.getTime() - lastUpdate.getTime()) / 1000;
      const isTooOldBatteryData = diff >= device.batteryLevelMaxUpdateInterval;

      if (isTooOldBatteryData) {
        return undefined;
      }

      return device;
    })
  );

  value$ = this.device$.pipe(
    map(device => {
      if (!device) {
        return this.transloco.translate("asset.no_data");
      }

      if (device.batteryLevel === undefined) {
        return this.transloco.translate("asset.no_data");
      }

      return `${device.batteryLevel}%`;
    }),
    startWith(this.transloco.translate("asset.loading")),
  );

  batteryLevel$ = this.device$.pipe(
    map<LorenDevice | undefined, BatteryLevel>(device => {
      const value = device?.batteryLevel;

      if (value === null || value === undefined) {
        return "UNKNOWN";
      }

      if (device.charging) {
        return "CHARGING";
      }

      if (value >= 0 && value <= 20) {
        return "LOW";
      }

      if (value > 20 && value < 50) {
        return "MEDIUM";
      }

      return "NORMAL";
    }),
    startWith("UNKNOWN" as BatteryLevel),
  );


  tooltip$ = this.device$.pipe(
    withLatestFrom(this.value$),
    map(([device, value]) => {
      const parts = [
        value,
      ];

      if (device) {
        parts.unshift(device.name);
      }

      return parts.join(': ');
    }),
  )
}
